import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import React, { useRef, useState } from 'react'

import { Body1 } from '../MaterialUi/Typo'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      gap: 8,
    },
  },
  input: {
    width: 56,
    height: 56,
    textAlign: 'center',
    fontSize: 24,
    border: '1px solid #ccc',
    borderRadius: 12,
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
      fontSize: 18,
    },
  },
  errorInput: {
    border: '1px solid red',
  },
  separator: {
    fontSize: 24,
    fontWeight: 'bold',
  },
}))

interface FlatsyOTPInputProps {
  length: number

  onChange?: (value: string) => void
  onComplete?: (value: string) => void

  autoFocus?: boolean
  className?: string
  disabled?: boolean
  hasError?: boolean

  separatorsGap?: number
}

const FlatsyOTPInput: React.FC<FlatsyOTPInputProps> = ({
  length,

  onChange,
  onComplete,

  autoFocus,
  className,
  disabled,
  hasError,

  separatorsGap,
}) => {
  const classes = useStyles()
  const inputsRef = useRef<(HTMLInputElement | null)[]>(Array.from({ length }).map((_) => null))
  const [otpValues, setOtpValues] = useState<string[]>(Array.from({ length }).map((_) => ''))

  const focusInput = (index: number) => {
    inputsRef.current[index]?.focus()
  }

  const updateStates = (newOtpValues: string[]) => {
    setOtpValues(newOtpValues)

    const newOtp = newOtpValues.join('')
    onChange?.(newOtp)
    if (newOtp.length === length) {
      onComplete?.(newOtp)
    }
  }

  const handleChange = (index: number, val: string) => {
    val = val.toUpperCase()
    if (!/^[A-Z0-9]$/.test(val)) {
      return
    }

    const newOtpValues = [...otpValues]
    newOtpValues[index] = val
    updateStates(newOtpValues)

    if (index < length - 1) {
      focusInput(index + 1)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const newOtpValues = [...otpValues]

      if (newOtpValues[index]) {
        newOtpValues[index] = ''
        updateStates(newOtpValues)
      } else if (index > 0) {
        newOtpValues[index - 1] = ''
        updateStates(newOtpValues)
        focusInput(index - 1)
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text').trim()
    if (!/^[a-zA-Z0-9]+$/.test(pasteData)) {
      return
    }

    const pasteDataFormatted = pasteData.padEnd(length, '').slice(0, length)

    updateStates(pasteDataFormatted.split(''))

    const focusIndex = Math.min(pasteData.length, length - 1)
    focusInput(focusIndex)
  }

  return (
    <div className={clsx(classes.container, className)}>
      {Array.from({ length }).map((_, i) => (
        <React.Fragment key={i}>
          {separatorsGap && i > 0 && i % separatorsGap === 0 && (
            <Body1 className={classes.separator}>-</Body1>
          )}
          <input
            type="text"
            disabled={disabled}
            maxLength={1}
            value={otpValues[i] || ''}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={i === 0 ? handlePaste : undefined}
            ref={(el) => {
              inputsRef.current[i] = el
            }}
            className={clsx(classes.input, { [classes.errorInput]: hasError })}
            inputMode="text"
            pattern="[a-zA-Z0-9]*"
            autoComplete="one-time-code"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus && i === 0}
          />
        </React.Fragment>
      ))}
    </div>
  )
}

export default FlatsyOTPInput
